import { ArrowRight, ArrowLeft } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { Modal, VbButton } from '@/components/vb';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useRef } from 'react';

type PaidProjectOnboardingProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const steps = [
    {
        img: 'paid-onboarding-1',
        title: 'Thanks for trusting AITS',
        subtext:
            'You’ve upgraded to full access - and we don’t take that lightly.<br/>From here, you’ll see exactly where your site stands, what matters most, and how to improve it - clearly, quickly, and with zero noise.<br />Let’s show you what’s unlocked.',
    },
    {
        img: 'paid-onboarding-2',
        title: 'See all 19 AI Trust Signals',
        subtext:
            "Full transparency across brand's authority, security, credibility, and more.<br/>No guesswork. No blind spots.",
    },
    {
        img: 'paid-onboarding-3',
        title: 'Tailored insights, on demand',
        subtext:
            'Your report includes a personalized analysis, AITS score,  improvement recommendations, and access to Marcus AI chat and executive summary',
    },
    {
        img: 'paid-onboarding-4',
        title: "You're ready",
        subtext:
            'Nothing to configure. No setup needed.<br />\n\nYour full trust report is one click away.',
    },
] as const;
const activeStep = 'w-10 h-1 rounded-2xl bg-vb-brand';
const notActiveStep = 'w-1 h-1 rounded-full bg-vb-brand-soft';

export const PaidProjectOnboardingDialog: React.FC<
    PaidProjectOnboardingProps
> = ({ open, onOpenChange }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [direction, setDirection] = useState<'left' | 'right'>('right');
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const touchStartXRef = useRef<number | null>(null);
    const touchStartYRef = useRef<number | null>(null);
    const touchActiveRef = useRef<boolean>(false);

    const { img, title, subtext } = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;

    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                setDirection('right');
                setCurrentStep(s => Math.min(s + 1, steps.length - 1));
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                setDirection('left');
                setCurrentStep(s => Math.max(s - 1, 0));
            }
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [open]);

    const goPrev = () => {
        setDirection('left');
        setCurrentStep((s) => Math.max(s - 1, 0));
    }

    const goNext = () => {
        setDirection('right');
        setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!isMobile) return;
        const t = e.touches[0];
        touchStartXRef.current = t.clientX;
        touchStartYRef.current = t.clientY;
        touchActiveRef.current = true;
    }

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!isMobile || !touchActiveRef.current) return;
        const t = e.changedTouches[0];
        const startX = touchStartXRef.current ?? t.clientX;
        const startY = touchStartYRef.current ?? t.clientY;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const threshold = 40;
        if (absDx > absDy && absDx > threshold) {
            if (dx < 0) {
                setDirection('right');
                setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
            } else {
                setDirection('left');
                setCurrentStep((s) => Math.max(s - 1, 0));
            }
        }
        touchStartXRef.current = null;
        touchStartYRef.current = null;
        touchActiveRef.current = false;
    }
    return (
        // Dismissal is locked to the corner close / final CTA — Radix's outside
        // click and Escape are both swallowed, as they were on the old dialog.
        <Modal
            open={open}
            onOpenChange={onOpenChange}
            srTitle={title}
            bare
            widthClassName="max-w-none"
            // Full-bleed sheet on mobile, centered card from md up. The `!` on the
            // radius is deliberate: `.vb-modal-panel` sets border-radius in CSS
            // that loads after the Tailwind utilities, so a plain `rounded-none`
            // would lose the cascade.
            className="h-[100vh] max-h-none w-full gap-0 !rounded-none p-0 md:h-[80vh] md:w-[70vw] md:!rounded-vb-lg"
            onInteractOutside={e => e.preventDefault()}
            onEscapeKeyDown={e => e.preventDefault()}
        >
            <div
                className="flex min-h-0 w-full flex-1 flex-col overflow-hidden py-8"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div className="h-1/2 w-full overflow-hidden md:h-auto">
                    <div key={`img-${currentStep}`} className={`${direction === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}`}>
                        <img src={`/paidOnboarding/${(isMobile ? img.replace('paid-onboarding-', 'paid-onboarding-mobile-') : img)}.svg`} alt="" className="h-full w-full" />
                    </div>
                </div>

                {/* Content + Navigation section: flex-1 */}
                <div className="flex flex-1 flex-col px-6 md:px-24">
                    {/* Title + Subtext (animated within modal bounds) */}
                    <div className="flex w-full flex-1 items-center overflow-hidden">
                        <div key={`copy-${currentStep}`} className={`flex w-full flex-col items-center gap-2 ${direction === 'right' ? 'animate-slide-in-right' : 'animate-slide-in-left'}`}>
                            <h2 className="text-center text-2xl font-medium text-vb-text">{title}</h2>
                            <div
                                className="max-w-[400px] text-center text-sm leading-[150%] text-vb-text-3"
                                dangerouslySetInnerHTML={{ __html: subtext }}
                            />
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between gap-3">
                        {/* Hidden rather than unmounted at the ends so the dots stay
                            optically centered between two equal-width slots. */}
                        <VbButton
                            variant="ghost"
                            size="sm"
                            aria-label="Previous"
                            onClick={goPrev}
                            disabled={currentStep === 0}
                            className={currentStep === 0 ? 'invisible' : undefined}
                        >
                            <ArrowLeft className="h-5 w-5 text-vb-brand" />
                        </VbButton>

                        <div className={`flex flex-1 items-center justify-center gap-3 px-6 ${isLastStep ? 'flex-col md:flex-row' : ''}`}>
                            {!isLastStep ? (
                                steps.map((step, i) => (
                                    <div
                                        key={step.img}
                                        className={i === currentStep ? activeStep : notActiveStep}
                                    />
                                ))
                            ) : (
                                <div className='flex flex-1 md:flex-auto flex-col md:flex-row gap-4 md:justify-center md:items-center md:mx-auto'>
                                    <VbButton
                                        variant="primary"
                                        size="lg"
                                        className="w-full min-h-12 md:min-h-0 md:w-[165px]"
                                        onClick={() => {
                                            onOpenChange(false);
                                            navigate('/');
                                        }}
                                    >
                                        View dashboard
                                    </VbButton>
                                </div>
                            )}
                        </div>

                        <VbButton
                            variant="ghost"
                            size="sm"
                            aria-label="Next"
                            onClick={goNext}
                            disabled={isLastStep}
                            className={isLastStep ? 'invisible' : undefined}
                        >
                            <ArrowRight className="h-5 w-5 text-vb-brand" />
                        </VbButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PaidProjectOnboardingDialog;
